/**
 * create_flow tool - Create a new Power Automate flow
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { CreateFlowRequest, FlowDefinition, ConnectionReference } from "../api/types.js";
import { validateFlow } from "../validator/index.js";
import { logger } from "../utils/logger.js";
import { preprocessFlowActions } from "../utils/expression-escape.js";
import { extractArmErrorMessage, ERROR_DETAIL_LIMIT } from "../utils/format.js";
import {
  describeConnectionAuthFailure,
  ensureConnectionParameters,
  environmentLabelFor,
  resolveLogicalConnectionNames,
} from "./connection-binding.js";

const triggerSchema = z.object({
  type: z.string().describe("Trigger type (e.g., 'Request', 'Recurrence', 'OpenApiConnection')"),
  kind: z.string().optional().describe("Trigger kind (e.g., 'Button', 'Http')"),
  inputs: z.record(z.string(), z.unknown()).optional().describe("Trigger inputs"),
  recurrence: z
    .object({
      frequency: z.enum(["Second", "Minute", "Hour", "Day", "Week", "Month"]),
      interval: z.number().min(1),
      startTime: z.string().optional(),
      timeZone: z.string().optional(),
    })
    .optional()
    .describe("Recurrence settings for scheduled triggers"),
}).passthrough(); // Preserve extra trigger properties: splitOn, conditions, metadata, etc.

const actionSchema = z.object({
  type: z.string().describe("Action type"),
  inputs: z.unknown().optional().describe("Action inputs - can be object, string, array, or any value"),
  runAfter: z
    .record(z.string(), z.array(z.enum(["Succeeded", "Failed", "Skipped", "TimedOut"])))
    .optional()
    .describe("Dependencies"),
}).passthrough(); // Preserve nested properties: actions, foreach, expression, else, cases (Foreach/Until/If/Switch/Scope)

const connectionRefSchema = z.object({
  connectionName: z.string().describe("Connection name in the environment"),
  id: z.string().describe("Connection resource ID"),
  source: z.enum(["Invoker", "Embedded"]).optional().default("Embedded"),
  tier: z.enum(["Standard", "Premium", "NotSpecified"]).optional().default("NotSpecified"),
});

export const createFlowInputSchema = z.object({
  displayName: z.string().describe("The display name for the new flow"),
  description: z.string().optional().describe("Optional description for the flow"),
  trigger: triggerSchema.describe("The trigger definition"),
  triggerName: z.string().optional().default("manual").describe("Name for the trigger (default: 'manual')"),
  actions: z
    .record(z.string(), actionSchema)
    .describe("Actions in the flow, keyed by action name"),
  connectionReferences: z
    .record(z.string(), connectionRefSchema)
    .optional()
    .describe("Connection references keyed by connection key"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
  validateBeforeCreate: z
    .boolean()
    .optional()
    .default(true)
    .describe("Validate the flow definition before creating (default: true)"),
});

export type CreateFlowInput = z.infer<typeof createFlowInputSchema>;

export const createFlowTool = {
  name: "create_flow",
  description:
    "Create a new Power Automate flow. Provide a trigger, actions, and connection references. Use search_connectors and get_action_schema to understand the required parameters for connector actions. The flow is created enabled (Started); use toggle_flow to stop it.",
  inputSchema: {
    type: "object" as const,
    properties: {
      displayName: {
        type: "string",
        description: "The display name for the new flow",
      },
      description: {
        type: "string",
        description: "Optional description shown in the Power Automate UI",
      },
      trigger: {
        type: "object",
        description: "The trigger definition with type, kind, inputs, and optional recurrence",
        properties: {
          type: { type: "string", description: "Trigger type" },
          kind: { type: "string", description: "Trigger kind" },
          inputs: { type: "object", description: "Trigger inputs" },
          recurrence: {
            type: "object",
            description: "Recurrence settings",
            properties: {
              frequency: { type: "string", enum: ["Second", "Minute", "Hour", "Day", "Week", "Month"] },
              interval: { type: "number", minimum: 1 },
              startTime: { type: "string" },
              timeZone: { type: "string" },
            },
          },
        },
        required: ["type"],
      },
      triggerName: {
        type: "string",
        description: "Name for the trigger (default: 'manual')",
      },
      actions: {
        type: "object",
        description: "Actions keyed by name. Each action has type, inputs, and runAfter.",
        additionalProperties: {
          type: "object",
          properties: {
            type: { type: "string" },
            inputs: { type: "object" },
            runAfter: { type: "object" },
          },
          required: ["type"],
        },
      },
      connectionReferences: {
        type: "object",
        description: "Connection references keyed by connection key",
        additionalProperties: {
          type: "object",
          properties: {
            connectionName: { type: "string" },
            id: { type: "string" },
            source: { type: "string", enum: ["Invoker", "Embedded"] },
            tier: { type: "string", enum: ["Standard", "Premium", "NotSpecified"] },
          },
          required: ["connectionName", "id"],
        },
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      validateBeforeCreate: {
        type: "boolean",
        description: "Validate the flow definition before creating (default: true)",
      },
    },
    required: ["displayName", "trigger", "actions"],
  },
};

export async function handleCreateFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = createFlowInputSchema.parse(input);

  logger.info({ displayName: parsed.displayName }, "Executing create_flow");

  // Build flow definition
  // Preprocess actions to fix payload objects that contain @{} expressions.
  const processedActions = preprocessFlowActions(parsed.actions);

  const definition: FlowDefinition = {
    $schema:
      "https://schema.management.azure.com/providers/Microsoft.Logic/schemas/2016-06-01/workflowdefinition.json#",
    contentVersion: "1.0.0.0",
    // The flows API rejects properties.description on create/update ("Invalid
    // JSON at path 'properties.description'") — descriptions live INSIDE the
    // workflow definition (verified live 2026-07-05).
    ...(parsed.description !== undefined ? { description: parsed.description } : {}),
    triggers: {
      [parsed.triggerName ?? "manual"]: parsed.trigger,
    },
    actions: processedActions as FlowDefinition["actions"],
  };

  // Build connection references
  let connectionReferences: Record<string, ConnectionReference> = {};
  if (parsed.connectionReferences) {
    for (const [key, ref] of Object.entries(parsed.connectionReferences)) {
      connectionReferences[key] = {
        connectionName: ref.connectionName,
        id: ref.id,
        source: ref.source ?? "Embedded",
        tier: ref.tier ?? "NotSpecified",
      };
    }
  }

  // A source "Invoker" reference carries a logical connectionName, not a real
  // connection id — resolve it against the environment's actual connections so
  // the API doesn't reject a connection that exists (see connection-binding.ts).
  // Fails open when connections can't be listed.
  const connectionResolutionNotes: string[] = [];
  if (Object.keys(connectionReferences).length > 0) {
    const resolution = await resolveLogicalConnectionNames(
      api,
      connectionReferences,
      parsed.environment,
      environmentLabelFor(api, parsed.environment),
    );
    if (resolution.missing.length > 0) {
      return `Flow creation failed: ${resolution.missing.join(" ")}`;
    }
    connectionReferences = resolution.refs;
    connectionResolutionNotes.push(...resolution.resolved);
  }

  // Add required parameters ($connections/$authentication) whenever the flow
  // binds connections or contains OpenApiConnection actions — the service
  // rejects connector-bearing definitions that don't declare them.
  ensureConnectionParameters(definition, connectionReferences);

  // Validate if requested
  if (parsed.validateBeforeCreate) {
    const validation = validateFlow(definition, connectionReferences);

    if (!validation.valid) {
      const lines = ["Flow validation failed:\n"];

      const schemaErrors = validation.schemaValidation.errors;
      const exprErrors = validation.expressionValidation.errors.filter((e) => e.severity === "error");
      const connErrors = validation.connectionValidation.errors.filter((e) => e.severity === "error");

      if (schemaErrors.length > 0) {
        lines.push("Schema issues:");
        schemaErrors.forEach((e) => lines.push(`  • ${e.path}: ${e.message}`));
        lines.push("");
      }

      if (exprErrors.length > 0) {
        lines.push("Expression issues:");
        exprErrors.forEach((e) => lines.push(`  • ${e.path}: ${e.message}`));
        lines.push("");
      }

      if (connErrors.length > 0) {
        lines.push("Connection issues:");
        connErrors.forEach((e) => lines.push(`  • ${e.path}: ${e.message}`));
        lines.push("");
      }

      return lines.join("\n");
    }
  }

  // Create the flow
  const flowRequest: CreateFlowRequest = {
    properties: {
      displayName: parsed.displayName,
      definition,
      connectionReferences: Object.keys(connectionReferences).length > 0 ? connectionReferences : undefined,
    },
  };

  try {
    const flow = await api.createFlow(flowRequest, parsed.environment);
    const resolutionSuffix =
      connectionResolutionNotes.length > 0
        ? `\nConnection references resolved: ${connectionResolutionNotes.join("; ")}`
        : "";
    return `Created "${flow.properties.displayName}" (${flow.name}) - ${flow.properties.state}${resolutionSuffix}`;
  } catch (error) {
    // Extract meaningful error message from API response
    const msg = error instanceof Error ? error.message : String(error);
    let details = "";
    let extracted: string | undefined;

    if (error && typeof error === "object" && "details" in error) {
      const errDetails = (error).details;
      // Parse the error body properly (nested/stringified {error: {message}}
      // forms) — the old regex clipped messages at the first escaped quote,
      // hiding e.g. the allowed enum values the user needs to fix the flow.
      extracted = extractArmErrorMessage(errDetails);
      if (extracted) {
        details = extracted;
      } else if (typeof errDetails === "string") {
        details = errDetails;
      } else if (errDetails && typeof errDetails === "object") {
        details = JSON.stringify(errDetails).slice(0, ERROR_DETAIL_LIMIT);
      }
    }

    // Parse common error patterns
    if (msg.includes("InvalidOpenApiFlow") || msg.includes("DynamicOperationRequestClientFailure")) {
      if (details.includes("GetTable") || details.includes("BadRequest")) {
        return `Flow creation failed: Invalid Excel/SharePoint parameters. Check drive ID, file ID, and table name.${extracted ? ` Details: ${extracted.slice(0, ERROR_DETAIL_LIMIT)}` : ""}`;
      }
      if (extracted) {
        return `Flow creation failed: ${extracted.slice(0, ERROR_DETAIL_LIMIT)}`;
      }
      return `Flow creation failed: Invalid flow definition. ${details.slice(0, ERROR_DETAIL_LIMIT)}`;
    }

    if (msg.includes("ConnectionAuthorizationFailed")) {
      // Name the environment that was checked and what actually exists for
      // this connector there — "create the connection" is misleading advice
      // when a healthy connection exists in a different environment.
      const enriched = await describeConnectionAuthFailure(
        api,
        `${msg} ${details}`,
        parsed.environment,
        environmentLabelFor(api, parsed.environment),
      );
      if (enriched) return `Flow creation failed: ${enriched}`;
      const connMatch = msg.match(/apis\/([^/'"]+)/);
      const connName = connMatch?.[1] ? connMatch[1].replace("shared_", "") : "a connector";
      return `Flow creation failed: Missing ${connName} connection. Create it at make.powerautomate.com > Data > Connections.`;
    }

    return `Flow creation failed: ${msg.slice(0, ERROR_DETAIL_LIMIT)}${details ? ` - ${details.slice(0, ERROR_DETAIL_LIMIT)}` : ""}`;
  }
}
